import { createRoot } from 'react-dom/client';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { api } from '@/utils/api';
import { ContextProvider } from '@/context';
import { ErrorBoundary } from '@/components';
// eslint-disable-next-line import/no-unresolved
import styles from '@/styles/styles.css?inline';
import { version } from '../package.json';
import App from './App';
import { generateCssVariables } from './utils/generateCssVariables';

const DEPLOY_ENV = import.meta.env.MODE || 'prod';
const VERSION = version || 'unknown';

// eslint-disable-next-line no-console
console.log(
  `Concord widget loaded. v${VERSION}${
    DEPLOY_ENV === 'prod' ? '' : ` ${DEPLOY_ENV}`
  }.`,
);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

function createStyles() {
  const branding = api?.config?.regionTemplate?.settings?.branding || {};
  const floatingButton =
    api?.config?.regionTemplate?.settings?.floatingButton || {};
  const { theme: cbTheme } =
    api?.config?.regionTemplate?.settings?.consent?.consentBanner || {};
  const { theme: pcTheme } =
    api?.config?.regionTemplate?.settings?.privacyCenter || {};

  // Generate CSS variables
  const cssVariables = generateCssVariables(
    branding,
    floatingButton,
    cbTheme,
    pcTheme,
  );

  // Check for custom styles in api.plugin.widget.config.styles
  // TODO: Add this to combinedStyles later and eventually
  // move to the server with other styles above.
  //   const customStyles = api?.plugin?.widget?.config?.styles || '';

  // Combine default styles and custom CSS variables
  // const combinedStyles = `${styles}\n${cssVariables}\n${customStyles}`;
  const combinedStyles = `${styles}\n${cssVariables}`;

  return combinedStyles;
}

function setupDOM(combinedStyles: string) {
  const habitat = document.createElement('div');
  habitat.style.display = 'block';

  const shadowRoot = habitat.attachShadow({ mode: 'open' });
  shadowRoot.host.id = 'cd-outer-hab';
  // Fix for fastclick shadow dom issue
  // https://github.com/ftlabs/fastclick/issues/604
  shadowRoot.host.className = 'needsclick';
  const appContainer = document.createElement('div');
  appContainer.id = 'cd-hab';

  const styleElement = document.createElement('style');
  styleElement.textContent = combinedStyles;
  shadowRoot.appendChild(styleElement);

  shadowRoot.appendChild(appContainer);
  document.body.appendChild(habitat);

  return { shadowRoot, appContainer };
}

function initializeApp() {
  api.onConcordConfigLoaded(() => {
    if (!api?.loaded) {
      return;
    }

    const combinedStyles = createStyles();
    const { appContainer } = setupDOM(combinedStyles);

    const root = createRoot(appContainer);

    root.render(
      <ErrorBoundary>
        <ContextProvider>
          <App />
        </ContextProvider>
      </ErrorBoundary>,
    );
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}
